import React, { useState, useEffect } from 'react';

const CommonTable = ({
  columns,
  data,
  itemsPerPageOptions = [10, 15, 25, 50, 100, 'All'],
  defaultItemsPerPage = (process.env.REACT_APP_PAGE_LIMIT ? parseInt(process.env.REACT_APP_PAGE_LIMIT, 10) : 15),
  showPagination = true,
  // Controlled / server-side pagination props
  page, // number (1-based) - if provided, component acts controlled for page
  onPageChange, // fn(newPage)
  itemsPerPage: itemsPerPageProp, // number or 'All' - controlled items per page
  onItemsPerPageChange, // fn(newItemsPerPage)
  totalItems: totalItemsProp, // when provided, treat as server-side total count
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);

  useEffect(() => {
    // reset to first page when data source changes (client-side)
    if (typeof page === 'undefined') setCurrentPage(1);
  }, [data]);

  const safeData = Array.isArray(data) ? data : [];
  const useControlledItemsPerPage = typeof itemsPerPageProp !== 'undefined';
  const effectiveItemsPerPage = useControlledItemsPerPage ? itemsPerPageProp : itemsPerPage;
  const useServerSide = typeof totalItemsProp !== 'undefined';

  const totalItems = useServerSide ? (Number(totalItemsProp) || 0) : safeData.length;
  const isShowAll = effectiveItemsPerPage === 'All';
  const totalPages = isShowAll ? 1 : Math.max(1, Math.ceil(totalItems / (isNaN(Number(effectiveItemsPerPage)) ? 1 : Number(effectiveItemsPerPage))));

  const effectivePage = typeof page === 'undefined' ? currentPage : page;

  const startIndex = isShowAll ? 0 : (effectivePage - 1) * (isNaN(Number(effectiveItemsPerPage)) ? 1 : Number(effectiveItemsPerPage));
  const endIndex = isShowAll ? totalItems : startIndex + (isNaN(Number(effectiveItemsPerPage)) ? 1 : Number(effectiveItemsPerPage));

  // If server-side (totalItemsProp provided) we expect `data` to already be the current page's rows.
  const currentData = useServerSide ? safeData : safeData.slice(startIndex, endIndex);

  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (effectivePage <= 3) {
        for (let i = 1; i <= maxPagesToShow; i++) {
          pages.push(i);
        }
      } else if (effectivePage >= totalPages - 2) {
        for (let i = totalPages - maxPagesToShow + 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        for (let i = effectivePage - 2; i <= effectivePage + 2; i++) {
          pages.push(i);
        }
      }
    }
    return pages;
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    if (typeof onPageChange === 'function') {
      onPageChange(page);
      return;
    }
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    const value = typeof newItemsPerPage === 'string' ? newItemsPerPage : String(newItemsPerPage);
    const resolved = value === 'All' ? 'All' : parseInt(value, 10);
    if (value !== 'All' && (isNaN(resolved) || resolved <= 0)) return;
    if (typeof onItemsPerPageChange === 'function') {
      onItemsPerPageChange(resolved === 'All' ? 'All' : resolved);
      if (typeof onPageChange === 'function') onPageChange(1);
      return;
    }
    setItemsPerPage(resolved === 'All' ? 'All' : resolved);
    setCurrentPage(1);
  };

  // Ensure currentPage is within bounds when data or itemsPerPage changes
  useEffect(() => {
    if (totalPages === 0) {
      if (typeof onPageChange === 'function') onPageChange(1);
      else setCurrentPage(1);
      return;
    }
    if (effectivePage > totalPages) {
      if (typeof onPageChange === 'function') onPageChange(totalPages);
      else setCurrentPage(totalPages);
    }
  }, [totalPages]);

  return (
    <div
      className="card"
      style={{
        borderRadius: '8px',
        border: '1px solid #e3e6ef',
        overflow: 'hidden',
      }}
    >
      <div className="card-body p-0">
        <div className="table-responsive">
          <table
            className="table mb-0"
          >
            <thead>
              <tr>
                {columns.map((col, index) => (
                  <th
                    key={index}
                    className="fw-semibold p-2 letterSpacing-1"
                    style={{
                      backgroundColor: '#D5D8E7',
                      color: '#667085',
                      border: '2px #000',
                      // padding: '12px 8px',
                      // letterSpacing: '0.5px',
                    }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentData.length > 0 ? (
                currentData.map((row, i) => (
                  <tr
                    key={row && (row._id || row.id) ? (row._id || row.id) : i}
                  >
                    {columns.map((col, j) => (
                      <td
                        key={j}
                      >
                        {row[col.key]}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="p-0"
                    colSpan={columns.length}
                  >
                    <div className="d-flex flex-column align-items-center">
                      <i className="fas fa-inbox fa-2x mb-3"></i>
                      <span>No data available</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination and entries info */}
      {showPagination && (
        <div
          className="d-flex justify-content-between align-items-center px-2 py-2"
        >
          {/* Show entries dropdown */}
          <div className="d-flex align-items-center">
            <span className="me-2">Show</span>
            <select
              className="form-select form-select-sm"
              style={{
                width: '80px',
                fontSize: '0.95rem',
                border: '1px solid #e3e6ef',
                borderRadius: '6px',
                padding: '2px 8px',
              }}
              value={effectiveItemsPerPage === 'All' ? 'All' : String(effectiveItemsPerPage)}
              onChange={e => handleItemsPerPageChange(e.target.value)}
            >
              {itemsPerPageOptions.map(option => (
                <option key={String(option)} value={option === 'All' ? 'All' : String(option)}>{option === 'All' ? 'All' : option}</option>
              ))}
            </select>
            <span className="ms-2">Entries</span>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <nav aria-label="Table pagination">
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${effectivePage === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: effectivePage === 1 ? '#b0b7c3' : '#252525',
                      cursor: effectivePage === 1 ? 'not-allowed' : 'pointer',
                    }}
                    onClick={() => handlePageChange(effectivePage - 1)}
                    disabled={effectivePage === 1}
                  >
                    &lt; Previous
                  </button>
                </li>
                {getPageNumbers().map(page => (
                  <li key={page} className={`page-item ${effectivePage === page ? 'active' : ''}`}>
                    <button
                      className="page-link mx-1"
                      style={{
                        border: 'none',
                          background: effectivePage === page ? '#2b8cfc' : 'transparent',
                          color: effectivePage === page ? '#fff' : '#252525',
                          borderRadius: '6px',
                          fontWeight: effectivePage === page ? 600 : 400,
                      }}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${effectivePage === totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: effectivePage === totalPages ? '#b0b7c3' : '#252525',
                      cursor: effectivePage === totalPages ? 'not-allowed' : 'pointer',
                    }}
                    onClick={() => handlePageChange(effectivePage + 1)}
                    disabled={effectivePage === totalPages}
                  >
                    Next &gt;
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </div>
      )}
    </div>
  );
};

export default CommonTable;